import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { getGarment, COLORS } from "@/data/garments";
import { STATUS_LABELS } from "@/data/credits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { urlToDataURL } from "@/utils/exportMockup";
import { Trash2, Download, Plus, Loader2, ImageOff, FileText, Pencil, FileDown } from "lucide-react";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const TECHNIQUES = ["Serigrafía", "DTG", "Vinilo textil", "Sublimación", "Bordado", "Transfer DTF"];
const STATUS_STYLES = {
  waiting: "bg-neutral-100 text-neutral-600",
  production: "bg-blue-50 text-[#002FA7]",
  done: "bg-green-50 text-green-700",
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mockups, setMockups] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [spec, setSpec] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/mockups");
      setMockups(data);
    } catch {
      toast.error("No se pudieron cargar los mockups");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const handleDelete = async (id) => {
    setMockups((m) => m.filter((x) => x.id !== id));
    try {
      await api.delete(`/mockups/${id}`);
      toast.success("Mockup eliminado");
    } catch {
      toast.error("Error al eliminar");
      load();
    }
  };

  const openSpec = (m) => {
    setEditing(m);
    setSpec({
      fabric: m.spec?.fabric || "",
      technique: m.spec?.technique || "",
      quantities: { ...Object.fromEntries(SIZES.map((s) => [s, m.spec?.quantities?.[s] ?? 0])) },
      status: m.spec?.status || "waiting",
    });
  };

  const saveSpec = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/mockups/${editing.id}`, spec);
      setMockups((ms) => ms.map((x) => (x.id === editing.id ? data : x)));
      toast.success("Ficha técnica guardada");
      setEditing(null);
    } catch {
      toast.error("No se pudo guardar la ficha");
    }
    setSaving(false);
  };

  const colorName = (v) => COLORS.find((c) => c.value === v)?.name || v;
  const totalUnits = (m) => Object.values(m.spec?.quantities || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  const generatePDF = async (m) => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.text("Ficha técnica de producción", 40, 54);
      doc.setFontSize(13); doc.setFont("helvetica", "normal"); doc.text(m.name, 40, 74);
      try { const img = await urlToDataURL(fileUrl(m.preview_path)); doc.addImage(img, "PNG", 40, 92, 220, 220); } catch {}
      let y = 112; const x = 290;
      const line = (label, val) => { doc.setFont("helvetica", "bold"); doc.text(label, x, y); doc.setFont("helvetica", "normal"); doc.text(String(val ?? "-"), x + 120, y); y += 22; };
      line("Prenda:", getGarment(m.garment).label);
      line("Color:", colorName(m.color));
      line("Tela:", m.spec?.fabric || "-");
      line("Técnica:", m.spec?.technique || "-");
      line("Estado:", STATUS_LABELS[m.spec?.status || "waiting"]);
      line("Total uds:", totalUnits(m));
      doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.text("Cantidades por talla", 40, 360);
      doc.setFontSize(12);
      const q = m.spec?.quantities || {};
      let cx = 40;
      SIZES.forEach((s) => { doc.setFont("helvetica", "bold"); doc.text(s, cx, 388); doc.setFont("helvetica", "normal"); doc.text(String(q[s] || 0), cx, 408); cx += 85; });
      doc.setFontSize(10); doc.setTextColor(150); doc.text("Generado con Mockup Studio", 40, 800);
      doc.save(`${m.name}-ficha.pdf`);
      toast.success("PDF generado");
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <div className="max-w-6xl w-full mx-auto px-6 py-12 flex-1">
        <div className="flex items-end justify-between mb-10 border-b border-border pb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Producción</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Mis mockups</h1>
          </div>
          <Button onClick={() => navigate("/")} className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a]" data-testid="new-mockup-btn">
            <Plus className="w-4 h-4" /> Nuevo mockup
          </Button>
        </div>

        {busy ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" /></div>
        ) : mockups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4 border border-dashed border-border rounded-sm" data-testid="empty-state">
            <ImageOff className="w-10 h-10 text-neutral-300" />
            <p className="text-neutral-500 max-w-sm">Aún no has guardado ningún mockup. Crea uno en el editor y pulsa "Generar mockup".</p>
            <Button onClick={() => navigate("/")} className="rounded-sm gap-2" data-testid="empty-create-btn"><Plus className="w-4 h-4" /> Crear el primero</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mockups-grid">
            {mockups.map((m) => (
              <div key={m.id} className="group border border-border rounded-sm overflow-hidden bg-white flex flex-col" data-testid={`mockup-card-${m.id}`}>
                <div className="aspect-square bg-[#F4F4F5] flex items-center justify-center overflow-hidden relative">
                  <img src={fileUrl(m.preview_path)} alt={m.name} className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" />
                  <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-sm ${STATUS_STYLES[m.spec?.status || "waiting"]}`} data-testid={`status-${m.id}`}>
                    {STATUS_LABELS[m.spec?.status || "waiting"]}
                  </span>
                </div>
                <div className="p-4 border-t border-border flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{m.name}</p>
                      <p className="text-xs uppercase tracking-wider text-neutral-500">{getGarment(m.garment).label} · {colorName(m.color)}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => navigate("/", { state: { mockup: m } })} data-testid={`edit-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                      <a href={fileUrl(m.preview_path)} download={`${m.name}.png`} target="_blank" rel="noreferrer">
                        <Button variant="ghost" size="icon" className="rounded-sm" data-testid={`download-${m.id}`}><Download className="w-4 h-4" /></Button>
                      </a>
                      <Button variant="ghost" size="icon" className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(m.id)} data-testid={`delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-neutral-500 border-t border-border/60 pt-3">
                    <span>{m.spec?.technique || "Sin técnica"} · {totalUnits(m)} uds</span>
                    <div className="flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-7 text-xs" onClick={() => generatePDF(m)} data-testid={`pdf-${m.id}`}>
                        <FileDown className="w-3.5 h-3.5" /> PDF
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm gap-1.5 h-7 text-xs" onClick={() => openSpec(m)} data-testid={`spec-${m.id}`}>
                        <FileText className="w-3.5 h-3.5" /> Ficha
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ficha técnica dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="spec-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Ficha técnica · {editing?.name}</DialogTitle>
          </DialogHeader>
          {spec && (
            <div className="flex flex-col gap-5 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Tipo de tela</Label>
                  <Input value={spec.fabric} onChange={(e) => setSpec({ ...spec, fabric: e.target.value })} placeholder="Algodón 180g" className="rounded-sm" data-testid="spec-fabric" />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Técnica</Label>
                  <Select value={spec.technique} onValueChange={(v) => setSpec({ ...spec, technique: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="spec-technique"><SelectValue placeholder="Elegir" /></SelectTrigger>
                    <SelectContent>
                      {TECHNIQUES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Cantidades por talla</Label>
                <div className="grid grid-cols-6 gap-2">
                  {SIZES.map((s) => (
                    <div key={s} className="flex flex-col items-center gap-1">
                      <span className="text-[11px] font-medium text-neutral-500">{s}</span>
                      <Input
                        type="number"
                        min={0}
                        value={spec.quantities[s]}
                        onChange={(e) => setSpec({ ...spec, quantities: { ...spec.quantities, [s]: Math.max(0, parseInt(e.target.value) || 0) } })}
                        className="rounded-sm text-center px-1 h-9"
                        data-testid={`spec-qty-${s}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Estado</Label>
                <Select value={spec.status} onValueChange={(v) => setSpec({ ...spec, status: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="spec-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="waiting">En espera</SelectItem>
                    <SelectItem value="production">En producción</SelectItem>
                    <SelectItem value="done">Completado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveSpec} disabled={saving} className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a]" data-testid="save-spec-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar ficha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
